import React, { useState, useEffect } from 'react';
import { useAuth } from 'react-oidc-context';
import DatePicker, { registerLocale } from 'react-datepicker';
import { es } from 'date-fns/locale';
import 'react-datepicker/dist/react-datepicker.css';
import PatientService from '../../services/patientService';
import SatCatalogService from '../../services/satCatalogService';
import Button from '../common/Button';
import ActionFooter from '../common/ActionFooter';
import { useAuthSync } from '../../hooks/useAuthSync';
import { getTodayDateString } from '../../utils';
import './PatientForm.css';

registerLocale('es', es);

const MaskedDateInput = React.forwardRef(({ onChange, ...props }, ref) => {
  const handleChange = (e) => {
    const raw = e.target.value.replace(/\D/g, '').slice(0, 8);
    let masked = '';
    for (let i = 0; i < raw.length; i++) {
      if (i === 2 || i === 4) masked += '/';
      masked += raw[i];
    }
    e.target.value = masked;
    if (onChange) onChange(e);
  };
  return <input ref={ref} {...props} onChange={handleChange} />;
});

const PatientForm = ({
  patient = null, 
  isModal = false, 
  onSuccess, 
  onCancel, 
  onClose 
}) => {
  const auth = useAuth();
  const { hasPermission, isMedicalDataEnabled } = useAuthSync();
  // In edit mode, hide contact fields if the user lacks the permission
  const hideContactFields = !!patient && !hasPermission('patient:viewcontactdetails');
  // Blood type and allergies are medical data; the backend ignores them when disabled
  const hideMedicalFields = !isMedicalDataEnabled();
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    gender: '',
    address: {
      street: '',
      city: '',
      state: '',
      zipCode: '',
      country: ''
    },
    dateOfBirth: '',
    cellPhone: '',
    occupation: '',
    estadoCivil: '',
    lengua: '',
    alergias: '',
    tipoSangre: '',
    emergencyContact: {
      name: '',
      phoneNumber: ''
    },
    notes: '',
    facturacion: {
      rfc: '',
      nombreReceptor: '',
      regimenFiscal: '',
      usoCfdi: '',
      codigoPostal: ''
    }
  });
  const [isLoading, setIsLoading] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [errors, setErrors] = useState({});
  const [hasSubmitted, setHasSubmitted] = useState(false);
  const [satCatalogs, setSatCatalogs] = useState({
    regimenFiscal: [],
    usoCfdi: []
  });

  // Load patient data if editing
  useEffect(() => {
    if (patient) {
      setFormData({
        firstName: patient.firstName || '',
        lastName: patient.lastName || '',
        email: patient.email || '',
        gender: patient.gender || '',
        address: {
          street: patient.address?.street || '',
          city: patient.address?.city || '',
          state: patient.address?.state || '',
          zipCode: patient.address?.zipCode || '',
          country: patient.address?.country || ''
        },
        dateOfBirth: patient.dateOfBirth && typeof patient.dateOfBirth === 'string' ? patient.dateOfBirth.split('T')[0] : '',
        cellPhone: patient.cellPhone || patient.phoneNumber || '', // Support both old and new field names
        occupation: patient.occupation || '',
        estadoCivil: patient.estadoCivil || '',
        lengua: patient.lengua || '',
        alergias: patient.alergias || '',
        tipoSangre: patient.tipoSangre || '',
        emergencyContact: {
          name: patient.emergencyContact?.name || '',
          phoneNumber: patient.emergencyContact?.phoneNumber || ''
        },
        notes: patient.notes || '',
        facturacion: {
          rfc: patient.facturacion?.rfc || '',
          nombreReceptor: patient.facturacion?.nombreReceptor || '',
          regimenFiscal: patient.facturacion?.regimenFiscal || '',
          usoCfdi: patient.facturacion?.usoCfdi || '',
          codigoPostal: patient.facturacion?.codigoPostal || ''
        }
      });
    }
  }, [patient]);

  // Load SAT catalogs
  useEffect(() => {
    const loadSatCatalogs = async () => {
      try {
        if (auth.user?.access_token) {
          const catalogs = await SatCatalogService.getAllSatCatalogs(auth.user.access_token);
          setSatCatalogs(catalogs);
        }
      } catch (error) {
        console.error('Error loading SAT catalogs:', error);
      }
    };

    loadSatCatalogs();
  }, [auth.user?.access_token]);

  const validateForm = () => {
    const newErrors = {};

    // First name validation (REQUIRED)
    if (!formData.firstName.trim()) {
      newErrors.firstName = 'El nombre es requerido';
    } else if (formData.firstName.trim().length < 2) {
      newErrors.firstName = 'El nombre debe tener al menos 2 caracteres';
    } else if (formData.firstName.trim().length > 50) {
      newErrors.firstName = 'El nombre no puede exceder 50 caracteres';
    }

    // Last name validation (REQUIRED)
    if (!formData.lastName.trim()) {
      newErrors.lastName = 'Los apellidos son requeridos';
    } else if (formData.lastName.trim().length < 2) {
      newErrors.lastName = 'Los apellidos deben tener al menos 2 caracteres';
    } else if (formData.lastName.trim().length > 50) {
      newErrors.lastName = 'Los apellidos no pueden exceder 50 caracteres';
    }

    // Date of birth validation (OPTIONAL)
    if (formData.dateOfBirth) {
      if (formData.dateOfBirth > getTodayDateString('UTC')) {
        newErrors.dateOfBirth = 'La fecha de nacimiento no puede ser futura';
      }
    }

    // Email validation (OPTIONAL - only validate format if provided, skip if field is hidden/masked)
    if (!hideContactFields && formData.email && !/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'El correo electrónico no es válido';
    }

    // Phone number validation (OPTIONAL - only validate format if provided, skip if field is hidden/masked)
    if (!hideContactFields && formData.cellPhone && !/^\+?[\d\s\-()]+$/.test(formData.cellPhone)) {
      newErrors.cellPhone = 'El número de celular no es válido';
    }

    // Emergency contact validation (OPTIONAL - only validate format if provided)
    if (formData.emergencyContact.phoneNumber && !/^\+?[\d\s\-()]+$/.test(formData.emergencyContact.phoneNumber)) {
      newErrors['emergencyContact.phoneNumber'] = 'El número de teléfono no es válido';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    
    if (name.includes('.')) {
      const [parent, child] = name.split('.');
      setFormData(prev => ({
        ...prev,
        [parent]: {
          ...prev[parent],
          [child]: value
        }
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: value
      }));
    }

    // Clear error when user starts typing, but only if form has been submitted
    if (hasSubmitted && errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    setHasSubmitted(true);
    
    if (!validateForm()) {
      return;
    }

    setIsLoading(true);
    setMessage({ type: '', text: '' });

    try {
      const token = auth.user?.access_token;
      
      // Prepare data for submission - only include allowed fields
      const submissionData = {
        firstName: formData.firstName.trim(),
        lastName: formData.lastName.trim(),
        ...(hideContactFields ? {} : { email: formData.email }),
        gender: formData.gender,
        address: formData.address,
        dateOfBirth: formData.dateOfBirth || null,
        ...(hideContactFields ? {} : { cellPhone: formData.cellPhone }),
        occupation: formData.occupation,
        estadoCivil: formData.estadoCivil,
        lengua: formData.lengua,
        ...(hideMedicalFields ? {} : { alergias: formData.alergias, tipoSangre: formData.tipoSangre }),
        emergencyContact: formData.emergencyContact,
        facturacion: formData.facturacion
      };
      
      // Add notes only if not empty
      if (formData.notes && formData.notes.trim() !== '') {
        submissionData.notes = formData.notes;
      }
      
      if (patient) {
        // Update existing patient
        await PatientService.updatePatient(patient.id, submissionData, token);
        setMessage({
          type: 'success',
          text: 'Paciente actualizado exitosamente'
        });
      } else {
        // Create new patient
        await PatientService.createPatient(submissionData, token);
        setMessage({
          type: 'success',
          text: 'Paciente creado exitosamente'
        });
      }

      // Call success callback if provided
      if (onSuccess) {
        onSuccess();
      }

      // Reset form if not in modal or if creating new patient
      if (!isModal || !patient) {
        setFormData({
          firstName: '',
          lastName: '',
          email: '',
          gender: '',
          address: {
            street: '',
            city: '',
            state: '',
            zipCode: '',
            country: ''
          },
          dateOfBirth: '',
          cellPhone: '',
          occupation: '',
          estadoCivil: '',
          lengua: '',
          alergias: '',
          tipoSangre: '',
          emergencyContact: {
            name: '',
            phoneNumber: ''
          },
          notes: '',
          facturacion: {
            nombreReceptor: '',
            regimenFiscal: '',
            usoCfdi: '',
            codigoPostal: ''
          }
        });
        setHasSubmitted(false);
        setErrors({});
      }
    } catch (error) {
      setMessage({
        type: 'error',
        text: error.message || 'Error al procesar la solicitud. Inténtalo de nuevo.'
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleCancel = () => {
    if (onCancel) {
      onCancel();
    }
  };

  const getFieldError = (fieldName) => {
    return hasSubmitted ? (errors[fieldName] || '') : '';
  };

  const renderActions = (inModal = false) => (
    <>
      <Button
        type="button"
        variant="secondary"
        onClick={handleCancel}
        disabled={isLoading}
      >
        Cancelar
      </Button>
      <Button
        type="submit"
        form={inModal ? undefined : 'patient-form'}
        variant="primary"
        loading={isLoading}
        icon="fas fa-save"
      >
        {isLoading ? (patient ? 'Actualizando...' : 'Creando...') : (patient ? 'Actualizar Paciente' : 'Crear Paciente')}
      </Button>
    </>
  );

  const renderFormContent = () => (
    <form id="patient-form" onSubmit={handleSubmit} className={`patient-form${hasSubmitted ? ' submitted' : ''}`}>
      {message.text && (
        <div className={`message ${message.type}`}>
          {message.text}
        </div>
      )}

      <div className="form-section">
        <h3>Información Personal</h3>
        
        <div className="form-row">
          <div className="form-group">
            <label htmlFor="firstName">Nombre *</label>
            <input
              type="text"
              id="firstName"
              name="firstName"
              value={formData.firstName}
              onChange={handleInputChange}
              className={getFieldError('firstName') ? 'error' : ''}
              required
            />
            {getFieldError('firstName') && (
              <span className="error-message">{getFieldError('firstName')}</span>
            )}
          </div>

          <div className="form-group">
            <label htmlFor="lastName">Apellidos *</label>
            <input
              type="text"
              id="lastName"
              name="lastName"
              value={formData.lastName}
              onChange={handleInputChange}
              className={getFieldError('lastName') ? 'error' : ''}
              required
            />
            {getFieldError('lastName') && (
              <span className="error-message">{getFieldError('lastName')}</span>
            )}
          </div>
        </div>

        <div className="form-row">
          {!hideContactFields && (
            <div className="form-group">
              <label htmlFor="email">Correo Electrónico</label>
              <input
                type="email"
                id="email"
                name="email"
                value={formData.email}
                onChange={handleInputChange}
                className={getFieldError('email') ? 'error' : ''}
              />
              {getFieldError('email') && (
                <span className="error-message">{getFieldError('email')}</span>
              )}
            </div>
          )}

          <div className="form-group">
            <label htmlFor="gender">Género</label>
            <select
              id="gender"
              name="gender"
              value={formData.gender}
              onChange={handleInputChange}
              className={getFieldError('gender') ? 'error' : ''}
            >
              <option value="">Seleccionar género</option>
              <option value="male">Masculino</option>
              <option value="female">Femenino</option>
              <option value="other">Otro</option>
              <option value="prefer-not-to-say">Prefiero no decir</option>
            </select>
            {getFieldError('gender') && (
              <span className="error-message">{getFieldError('gender')}</span>
            )}
          </div>
        </div>

        <div className="form-row">
          <div className="form-group">
            <label htmlFor="dateOfBirth">Fecha de Nacimiento</label>
            <DatePicker
              id="dateOfBirth"
              selected={formData.dateOfBirth ? new Date(formData.dateOfBirth + 'T00:00:00') : null}
              onChange={(date) => {
                if (date) {
                  const y = date.getFullYear();
                  const m = String(date.getMonth() + 1).padStart(2, '0');
                  const d = String(date.getDate()).padStart(2, '0');
                  setFormData(prev => ({ ...prev, dateOfBirth: `${y}-${m}-${d}` }));
                } else {
                  setFormData(prev => ({ ...prev, dateOfBirth: '' }));
                }
                if (hasSubmitted && errors.dateOfBirth) {
                  setErrors(prev => ({ ...prev, dateOfBirth: '' }));
                }
              }}
              locale="es"
              dateFormat="dd/MM/yyyy"
              showYearDropdown
              showMonthDropdown
              dropdownMode="select"
              maxDate={new Date(`${getTodayDateString('UTC')}T00:00:00`)}
              minDate={new Date(1900, 0, 1)}
              placeholderText="DD/MM/AAAA"
              isClearable
              autoComplete="off"
              className={getFieldError('dateOfBirth') ? 'error' : ''}
              customInput={<MaskedDateInput />}
            />
            {getFieldError('dateOfBirth') && (
              <span className="error-message">{getFieldError('dateOfBirth')}</span>
            )}
          </div>

          {!hideContactFields && (
            <div className="form-group">
              <label htmlFor="cellPhone">Celular</label>
              <input
                type="tel"
                id="cellPhone"
                name="cellPhone"
                value={formData.cellPhone}
                onChange={handleInputChange}
                placeholder="55 5555 5555"
                maxLength={20}
                className={getFieldError('cellPhone') ? 'error' : ''}
              />
              {getFieldError('cellPhone') && (
                <span className="error-message">{getFieldError('cellPhone')}</span>
              )}
            </div>
          )}
        </div>

        <div className="form-row">
          <div className="form-group">
            <label htmlFor="occupation">Ocupación</label>
            <input
              type="text"
              id="occupation"
              name="occupation"
              value={formData.occupation}
              onChange={handleInputChange}
              className={getFieldError('occupation') ? 'error' : ''}
            />
            {getFieldError('occupation') && (
              <span className="error-message">{getFieldError('occupation')}</span>
            )}
          </div>

          <div className="form-group">
            <label htmlFor="estadoCivil">Estado Civil</label>
            <select
              id="estadoCivil"
              name="estadoCivil"
              value={formData.estadoCivil}
              onChange={handleInputChange}
              className={getFieldError('estadoCivil') ? 'error' : ''}
            >
              <option value="">Seleccionar estado civil</option>
              <option value="Soltero">Soltero</option>
              <option value="Casado">Casado</option>
              <option value="Viudo">Viudo</option>
              <option value="Union Libre">Unión Libre</option>
            </select>
            {getFieldError('estadoCivil') && (
              <span className="error-message">{getFieldError('estadoCivil')}</span>
            )}
          </div>
        </div>

        <div className="form-row">
          <div className="form-group">
            <label htmlFor="lengua">Lengua</label>
            <input
              type="text"
              id="lengua"
              name="lengua"
              value={formData.lengua}
              onChange={handleInputChange}
              placeholder="Idioma principal del paciente"
              className={getFieldError('lengua') ? 'error' : ''}
            />
            {getFieldError('lengua') && (
              <span className="error-message">{getFieldError('lengua')}</span>
            )}
          </div>

          {!hideMedicalFields && (
            <div className="form-group">
              <label htmlFor="tipoSangre">Tipo de Sangre</label>
              <select
                id="tipoSangre"
                name="tipoSangre"
                value={formData.tipoSangre}
                onChange={handleInputChange}
                className={getFieldError('tipoSangre') ? 'error' : ''}
              >
                <option value="">Seleccionar tipo de sangre</option>
                <option value="A+">A+</option>
                <option value="A-">A-</option>
                <option value="B+">B+</option>
                <option value="B-">B-</option>
                <option value="AB+">AB+</option>
                <option value="AB-">AB-</option>
                <option value="O+">O+</option>
                <option value="O-">O-</option>
              </select>
              {getFieldError('tipoSangre') && (
                <span className="error-message">{getFieldError('tipoSangre')}</span>
              )}
            </div>
          )}
        </div>

        {!hideMedicalFields && (
          <div className="form-row">
            <div className="form-group full-width">
              <label htmlFor="alergias">Alergias</label>
              <textarea
                id="alergias"
                name="alergias"
                value={formData.alergias}
                onChange={handleInputChange}
                placeholder="Alergias conocidas del paciente"
                rows="3"
                maxLength="500"
                className={getFieldError('alergias') ? 'error' : ''}
              />
              <div className="char-count">
                {formData.alergias.length}/500 caracteres
              </div>
              {getFieldError('alergias') && (
                <span className="error-message">{getFieldError('alergias')}</span>
              )}
            </div>
          </div>
        )}
      </div>

      <div className="form-section">
        <h3>Dirección</h3>
        
        <div className="form-row">
          <div className="form-group">
            <label htmlFor="address.street">Calle</label>
            <input
              type="text"
              id="address.street"
              name="address.street"
              value={formData.address.street}
              onChange={handleInputChange}
              className={getFieldError('address.street') ? 'error' : ''}
            />
            {getFieldError('address.street') && (
              <span className="error-message">{getFieldError('address.street')}</span>
            )}
          </div>

          <div className="form-group">
            <label htmlFor="address.city">Ciudad</label>
            <input
              type="text"
              id="address.city"
              name="address.city"
              value={formData.address.city}
              onChange={handleInputChange}
              className={getFieldError('address.city') ? 'error' : ''}
            />
            {getFieldError('address.city') && (
              <span className="error-message">{getFieldError('address.city')}</span>
            )}
          </div>
        </div>

        <div className="form-row">
          <div className="form-group">
            <label htmlFor="address.state">Estado</label>
            <input
              type="text"
              id="address.state"
              name="address.state"
              value={formData.address.state}
              onChange={handleInputChange}
              className={getFieldError('address.state') ? 'error' : ''}
            />
            {getFieldError('address.state') && (
              <span className="error-message">{getFieldError('address.state')}</span>
            )}
          </div>

          <div className="form-group">
            <label htmlFor="address.zipCode">Código Postal</label>
            <input
              type="text"
              id="address.zipCode"
              name="address.zipCode"
              value={formData.address.zipCode}
              onChange={handleInputChange}
              className={getFieldError('address.zipCode') ? 'error' : ''}
            />
            {getFieldError('address.zipCode') && (
              <span className="error-message">{getFieldError('address.zipCode')}</span>
            )}
          </div>
        </div>

        <div className="form-row">
          <div className="form-group">
            <label htmlFor="address.country">País</label>
            <input
              type="text"
              id="address.country"
              name="address.country"
              value={formData.address.country}
              onChange={handleInputChange}
              className={getFieldError('address.country') ? 'error' : ''}
            />
            {getFieldError('address.country') && (
              <span className="error-message">{getFieldError('address.country')}</span>
            )}
          </div>

          <div className="form-group">
            {/* Campo vacío para mantener el balance visual */}
          </div>
        </div>
      </div>

      <div className="form-section">
        <h3>Contacto de Emergencia</h3>
        
        <div className="form-row">
          <div className="form-group">
            <label htmlFor="emergencyContact.name">Nombre del Contacto</label>
            <input
              type="text"
              id="emergencyContact.name"
              name="emergencyContact.name"
              value={formData.emergencyContact.name}
              onChange={handleInputChange}
              className={getFieldError('emergencyContact.name') ? 'error' : ''}
            />
            {getFieldError('emergencyContact.name') && (
              <span className="error-message">{getFieldError('emergencyContact.name')}</span>
            )}
          </div>

          <div className="form-group">
            <label htmlFor="emergencyContact.phoneNumber">Teléfono del Contacto</label>
            <input
              type="tel"
              id="emergencyContact.phoneNumber"
              name="emergencyContact.phoneNumber"
              value={formData.emergencyContact.phoneNumber}
              onChange={handleInputChange}
              placeholder="55 5555 5555"
              className={getFieldError('emergencyContact.phoneNumber') ? 'error' : ''}
            />
            {getFieldError('emergencyContact.phoneNumber') && (
              <span className="error-message">{getFieldError('emergencyContact.phoneNumber')}</span>
            )}
          </div>
        </div>
      </div>

      <div className="form-section">
        <h3>Información de Facturación</h3>
        
        <div className="form-row">
          <div className="form-group">
            <label htmlFor="facturacion.nombreReceptor">Nombre del Receptor</label>
            <input
              type="text"
              id="facturacion.nombreReceptor"
              name="facturacion.nombreReceptor"
              value={formData.facturacion.nombreReceptor}
              onChange={handleInputChange}
              placeholder="Nombre completo para facturación"
              maxLength="300"
            />
          </div>

          <div className="form-group">
            <label htmlFor="facturacion.rfc">RFC</label>
            <input
              type="text"
              id="facturacion.rfc"
              name="facturacion.rfc"
              value={formData.facturacion.rfc}
              onChange={handleInputChange}
              placeholder="RFC del receptor"
              maxLength="20"
            />
          </div>

          <div className="form-group">
            <label htmlFor="facturacion.codigoPostal">Código Postal</label>
            <input
              type="text"
              id="facturacion.codigoPostal"
              name="facturacion.codigoPostal"
              value={formData.facturacion.codigoPostal}
              onChange={handleInputChange}
              placeholder="12345"
              pattern="[0-9]{5}"
              maxLength="5"
            />
          </div>
        </div>

        <div className="form-row">
          <div className="form-group">
            <label htmlFor="facturacion.regimenFiscal">Régimen Fiscal</label>
            <select
              id="facturacion.regimenFiscal"
              name="facturacion.regimenFiscal"
              value={formData.facturacion.regimenFiscal}
              onChange={handleInputChange}
            >
              <option value="">Seleccionar régimen fiscal</option>
              {satCatalogs.regimenFiscal.map((regimen) => (
                <option key={regimen.codigo} value={regimen.codigo}>
                  {regimen.codigo} - {regimen.descripcion}
                </option>
              ))}
            </select>
          </div>

          <div className="form-group">
            <label htmlFor="facturacion.usoCfdi">Uso de CFDI</label>
            <select
              id="facturacion.usoCfdi"
              name="facturacion.usoCfdi"
              value={formData.facturacion.usoCfdi}
              onChange={handleInputChange}
            >
              <option value="">Seleccionar uso de CFDI</option>
              {satCatalogs.usoCfdi.map((uso) => (
                <option key={uso.codigo} value={uso.codigo}>
                  {uso.codigo} - {uso.descripcion}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>

      <div className="form-section">
        <h3>Notas Adicionales</h3>
        
        <div className="form-row">
          <div className="form-group full-width">
            <label htmlFor="notes">Notas</label>
            <textarea
              id="notes"
              name="notes"
              value={formData.notes}
              onChange={handleInputChange}
              placeholder={hideMedicalFields
                ? "Información adicional sobre el paciente, preferencias, etc."
                : "Información adicional sobre el paciente, alergias, preferencias, etc."}
              rows="4"
              maxLength="1000"
            />
            <div className="char-count">
              {formData.notes.length}/1000 caracteres
            </div>
          </div>
        </div>
      </div>

      {/* Spacer so the last section clears the fixed ActionFooter on all devices */}
      <div style={{ height: '6rem' }} aria-hidden="true" />

    </form>
  );

  if (isModal) {
    return (
      <div className="modal-overlay" onClick={onClose}>
        <div className="modal-content" onClick={(e) => e.stopPropagation()}>
          <div className="modal-header">
            <h2>{patient ? 'Editar Paciente' : 'Nuevo Paciente'}</h2>
            <button className="modal-close" onClick={onClose}>
              <i className="fas fa-times"></i>
            </button>
          </div>
          <div className="modal-body">
            {renderFormContent()}
          </div>
          <div className="modal-footer">
            <div className="form-actions">
              <div className="btn-group">
                {renderActions(true)}
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="patient-form-container">
      <div className="patient-form-header">
        <h1>{patient ? 'Editar Paciente' : 'Nuevo Paciente'}</h1>
        <p>{patient ? 'Actualiza la información del paciente' : 'Agrega un nuevo paciente al sistema'}</p>
      </div>
      {renderFormContent()}
      <ActionFooter>
        {renderActions()}
      </ActionFooter>
    </div>
  );
};

export default PatientForm; 